import InputWithSlider from "@components/InputWithSlider";
import { Undo } from "@icon-park/svg";
import { formatMoney, formatPercent } from "@justhome/common";
import { Button, Icon, InfoIcon, NumberField } from "@justhome/react-ui";
import { FC, useCallback, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import CreateOneTimeRepayment from "./CreateOneTimeRepayment";
import CreateRepaymentRateChange from "./CreateRepaymentRateChange";
import FormArrayItem from "./FormArrayItem";
import { RepaymentCalculatorFormValue, RepaymentRateChange, SpecialRepayment } from "./types";
interface RepaymentCalculatorFormProps {
  onChange: (values: RepaymentCalculatorFormValue) => void;
  secondaryRepaymentRate: number | undefined;
  defaultValues: RepaymentCalculatorFormValue;
}
const RepaymentCalculatorForm: FC<RepaymentCalculatorFormProps> = ({
  onChange,
  secondaryRepaymentRate,
  defaultValues
}) => {
  const [showOneTimeRepaymentModal, setShowOneTimeRepaymentModal] = useState(false);
  const [showRepaymentRateChangeModal, setShowRepaymentRateChangeModal] = useState(false);
  const [selectedSpecialRepayment, setSelectedSpecialRepayment] = useState<SpecialRepayment | undefined>();
  const [selectedRepaymentRateChange, setSelectedRepaymentRateChange] = useState<RepaymentRateChange | undefined>();
  const [isSecondaryRepaymentRateLocked, setIsSecondaryRepaymentRateLocked] = useState(true);
  const {
    control,
    reset,
    formState,
    watch,
    setValue
  } = useForm<RepaymentCalculatorFormValue>({
    defaultValues
  });
  const fixedInterestPeriodYears = watch("fixedInterestPeriodYears");
  const specialRepaymentsField = useFieldArray({
    control,
    name: "specialRepayments"
  });
  const repaymentRateChangeField = useFieldArray({
    control,
    name: "repaymentRateChange"
  });
  const handleLockSecondaryRepaymentRate = useCallback(() => {
    setIsSecondaryRepaymentRateLocked(locked => !locked);
  }, [setIsSecondaryRepaymentRateLocked]);
  useEffect(() => {
    if (isSecondaryRepaymentRateLocked && typeof secondaryRepaymentRate !== "undefined") {
      setValue("secondaryRepaymentRate", secondaryRepaymentRate, {
        shouldDirty: false
      });
    }
  }, [setValue, isSecondaryRepaymentRateLocked, secondaryRepaymentRate]);
  useEffect(() => {
    let lastValue: RepaymentCalculatorFormValue | undefined;
    const {
      unsubscribe
    } = watch(values => {
      if (!(values.loanAmount && values.interestRate && values.repaymentRate && values.secondaryInterestRate && values.secondaryRepaymentRate)) {
        return;
      }
      if (JSON.stringify(values) !== JSON.stringify(lastValue)) {
        onChange(values as RepaymentCalculatorFormValue);
        lastValue = values as RepaymentCalculatorFormValue;
      }
    });
    return () => unsubscribe();
  }, [watch, onChange]);
  const handleReset = () => {
    reset(defaultValues);
    setIsSecondaryRepaymentRateLocked(true);
  };
  const handleAddSpecialRepayment = () => {
    setSelectedSpecialRepayment(undefined);
    setShowOneTimeRepaymentModal(true);
  };
  const handleEditSpecialRepayment = (value: SpecialRepayment) => {
    setSelectedSpecialRepayment(value);
    setShowOneTimeRepaymentModal(true);
  };
  const handleAddRepaymentRateChange = () => {
    setSelectedRepaymentRateChange(undefined);
    setShowRepaymentRateChangeModal(true);
  };
  const handleEditRepaymentRateChange = (value_0: RepaymentRateChange) => {
    setSelectedRepaymentRateChange(value_0);
    setShowRepaymentRateChangeModal(true);
  };
  const handleDeleteRepaymentRateChange = (value_1: RepaymentRateChange) => {
    const index = repaymentRateChangeField.fields.findIndex(r => r.id === value_1.id);
    repaymentRateChangeField.remove(index);
  };
  const handleUpsertSpecialRepayment = (value_2: SpecialRepayment) => {
    const index_0 = specialRepaymentsField.fields.findIndex(r_0 => r_0.id === value_2.id);
    if (index_0 >= 0) {
      specialRepaymentsField.update(index_0, value_2);
    } else {
      specialRepaymentsField.append(value_2);
    }
    setShowOneTimeRepaymentModal(false);
  };
  const handleUpsertRepaymentRateChange = (value_3: RepaymentRateChange) => {
    const index_1 = repaymentRateChangeField.fields.findIndex(r_1 => r_1.id === value_3.id);
    if (index_1 >= 0) {
      repaymentRateChangeField.update(index_1, value_3);
    } else {
      repaymentRateChangeField.append(value_3);
    }
    setShowRepaymentRateChangeModal(false);
  };
  const handleDeleteSpecialRepayment = (value_4: SpecialRepayment) => {
    const index_2 = specialRepaymentsField.fields.findIndex(r_2 => r_2.id === value_4.id);
    specialRepaymentsField.remove(index_2);
  };
  return <>
      {showOneTimeRepaymentModal && <CreateOneTimeRepayment defaultValues={selectedSpecialRepayment} control={control} isOpen onSubmit={handleUpsertSpecialRepayment} setIsOpen={setShowOneTimeRepaymentModal} />}
      {showRepaymentRateChangeModal && <CreateRepaymentRateChange defaultValues={selectedRepaymentRateChange} control={control} isOpen onSubmit={handleUpsertRepaymentRateChange} setIsOpen={setShowRepaymentRateChangeModal} />}
      {formState.isDirty && <Button className="absolute right-none top-2m" onClick={handleReset} iconEnd={<Icon icon={Undo} />} variant="ghost" size="sm" title="Zurücksetzen"></Button>}

      <form className="flex flex-col gap-s">
        <Controller name="loanAmount" control={control} render={({
        field
      }) => <NumberField size="sm" {...field} label="Darlehensbetrag" suffix="€" />} />
        <p className="text-app-h3 mt-2xs">Erste Finanzierung</p>
        <InputWithSlider info="Für diesen Zeitraum sind sind in der ersten Finanzierung die Zinsen festgeschrieben." control={control} name="fixedInterestPeriodYears" label="Zinsbindung" min={2} max={30} step={1} suffix="Jahre" />
        <InputWithSlider control={control} name="interestRate" label="Sollzins" min={0.2} max={6} step={0.1} suffix="%" />
        <InputWithSlider control={control} name="repaymentRate" label="Anfängliche Tilgung" min={1} max={10} step={0.1} suffix="%" />
        <InputWithSlider control={control} name="yearlySpecialRepayment" label="Jährliche Sondertilgung" min={0} max={25000} step={1250} suffix="€" info="Betrag, der jedes Jahr zusätzlich getilgt werden soll (freiwillige Tilgung)" />
        {specialRepaymentsField.fields.map(field_0 => <FormArrayItem value={field_0} key={field_0.id} formatValue={value_5 => `${value_5.year}, Einmalzahlung von ${formatMoney(value_5.amount)}`} onEdit={handleEditSpecialRepayment} onDelete={handleDeleteSpecialRepayment} />)}
        <div className="flex items-center gap-2xs text-app-body-1">
          <Button variant="ghost" iconStart="Plus" size="sm" paddingX={false} onClick={handleAddSpecialRepayment}>
            Einmalzahlung hinzufügen
          </Button>
          <InfoIcon>
            Eine oder mehrere freiwillige Einmalzahlungen zur schnelleren
            Tilgung des Darlehens. Es wird eine Einzahlung zum jeweiligen Ende
            des Kalenderjahres angenommen.
          </InfoIcon>
        </div>
        {repaymentRateChangeField.fields.map(field_1 => <FormArrayItem value={field_1} key={field_1.id} formatValue={value_6 => `${value_6.year}, Tilgungswechsel auf ${formatPercent(value_6.repaymentRate, "de")}`} onEdit={handleEditRepaymentRateChange} onDelete={handleDeleteRepaymentRateChange} />)}
        <div className="flex items-center gap-2xs text-app-body-1">
          <Button variant="ghost" iconStart="Plus" size="sm" paddingX={false} onClick={handleAddRepaymentRateChange}>
            Tilgungswechsel hinzufügen
          </Button>
        </div>

        <p className="text-app-h3 mt-2xs">
          Anschlussfinanzierung{" "}
          <span className="text-app-body-2">
            (nach {fixedInterestPeriodYears} Jahren)
          </span>
        </p>
        <InputWithSlider control={control} name="secondaryInterestRate" label="Sollzins nach Zinsbindung" min={0.2} max={6} step={0.1} suffix="%" />
        <InputWithSlider control={control} name="secondaryRepaymentRate" label="Tilgung nach Zinsbindung" min={1} max={20} step={0.1} suffix="%" disabled={isSecondaryRepaymentRateLocked} appendix={<Button onClick={handleLockSecondaryRepaymentRate} iconStart={isSecondaryRepaymentRateLocked ? "Lock" : "Unlock"} variant="outline_primary" size="sm"></Button>} />
      </form>
    </>;
};
export default RepaymentCalculatorForm;